import type {AjustesDeDocumentos, ListaDePlantillas, TipoDePlantilla} from '@cnc/contracts';
import {useEffect, useRef, useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {
  rutaDePlantilla,
  useActivarPlantilla,
  useAjustesDeDocumentos,
  useGuardarAjustesDeDocumentos,
  usePlantillas,
  useSubirPlantilla,
} from '../api/documentos';
import {AYUDA_DOCUMENTOS as D} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PistaDeSoltar, ZonaParaSoltar} from '../componentes/zona-para-soltar';
import {tr, type Texto} from '../i18n/idioma';
import {fechaDeInstante, numero} from '../utilidades/formato';

const campo = 'mt-1 w-full rounded-md border border-linea px-3 py-2 text-sm text-tinta';
const boton = 'rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const botonSuave = 'rounded-md border border-linea bg-white px-2.5 py-1 text-xs hover:bg-papel disabled:opacity-40';

const VACIO: AjustesDeDocumentos = {
  firmante: {nombre: '', cargo: '', nombreCorto: '', cargoCorto: ''},
  vendedor: {nombre: '', razonSocial: '', direccion: '', ciudad: '', nit: ''},
  consignatario: {lineas: [''], textoCarta: ''},
  plantillaCarta: {textoFirmante: '', textoCargo: ''},
};

// Administracion de documentos: plantillas (versionadas) y datos del
// firmante, vendedor y consignatario de los documentos de aduana.
export function PaginaDocumentosAdmin() {
  return (
    <section className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Documentos', 'Documents')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Las plantillas con las que se generan los documentos y los datos que llevan los documentos de aduana. Nada de esto sube a GitHub: vive en el almacén y en la base de datos.',
            'The templates used to generate the documents and the data printed on the customs documents. None of this goes to GitHub: it lives in the file store and in the database.',
          )}
        </p>
      </div>
      <Plantillas />
      <Ajustes />
    </section>
  );
}

// Sin empresa: las plantillas generales. Con empresa (su ficha en Empresas):
// logo, firma y certificado de ensayo propios (decision 0061).
export function Plantillas({empresa}: {empresa?: {id: number; nombre: string}}) {
  const empresaId = empresa?.id ?? null;
  const plantillas = usePlantillas(empresaId);
  const subir = useSubirPlantilla();
  const activar = useActivarPlantilla();
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [hecho, setHecho] = useState<string | null>(null);

  const cargar = async (tipo: TipoDePlantilla, archivo: File, notas: string) => {
    setMensaje(null);
    setAvisos([]);
    setHecho(null);
    try {
      const r = await subir.mutateAsync({tipo, archivo, notas, empresaId});
      setHecho(
        tr(
          `Versión ${r.plantilla.version} de "${archivo.name}" guardada y en uso.`,
          `Version ${r.plantilla.version} of "${archivo.name}" saved and in use.`,
        ),
      );
      setAvisos(r.avisos);
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi
          ? [error.message, ...(error.detalles ?? [])].join(' ')
          : tr('No se pudo subir la plantilla.', 'Could not upload the template.'),
      );
    }
  };

  return (
    <div className="space-y-3" data-prueba={empresa ? 'documentos-de-la-empresa' : undefined}>
      <h2 className="font-medium">
        {empresa ? tr('Documentos de la empresa', 'Company documents') : tr('Plantillas', 'Templates')}
        {empresa && <NotaDeAyuda texto={D.deLaEmpresa} />}
      </h2>
      <p className="text-xs text-apagado">
        {empresa
          ? tr(
              `Logo y firma que van en los documentos Bluequest cuando ${empresa.nombre} es el vendedor, y su certificado de ensayo cuando es el exportador. Si no tiene los suyos, se usan los generales de Administración → Documentos.`,
              `Logo and signature on the Bluequest documents when ${empresa.nombre} is the seller, and its assay certificate when it is the exporter. Without its own, the general ones from Administration → Documents are used.`,
            )
          : tr(
              'Cada plantilla se guarda tal cual se sube (byte a byte) y se comprueba que tenga lo que el generador busca. Las versiones anteriores se conservan y se pueden volver a poner en uso.',
              'Each template is saved exactly as uploaded (byte for byte) and checked for what the generator looks for. Previous versions are kept and can be put back in use.',
            )}
      </p>
      {mensaje && <p className="text-sm text-alerta">{mensaje}</p>}
      {hecho && <p className="text-sm text-verde-oscuro">{hecho}</p>}
      {avisos.map(a => (
        <p key={a} className="text-xs text-apagado">
          ⚠ {a}
        </p>
      ))}
      {plantillas.isError && <p className="text-sm text-alerta">{plantillas.error.message}</p>}
      {plantillas.data && (
        <div className="grid gap-3 md:grid-cols-2">
          {plantillas.data.plantillas.map(p => (
            <TarjetaDePlantilla
              key={p.tipo}
              plantilla={p}
              empresaId={empresaId}
              pendiente={subir.isPending || activar.isPending}
              onSubir={(archivo, notas) => void cargar(p.tipo, archivo, notas)}
              onActivar={id =>
                void activar
                  .mutateAsync({tipo: p.tipo, id, empresaId})
                  .catch(e =>
                    setMensaje(e instanceof ErrorDeApi ? e.message : tr('No se pudo activar.', 'Could not activate.')),
                  )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TarjetaDePlantilla({
  plantilla: p,
  empresaId,
  pendiente,
  onSubir,
  onActivar,
}: {
  plantilla: ListaDePlantillas['plantillas'][number];
  empresaId: number | null;
  pendiente: boolean;
  onSubir: (archivo: File, notas: string) => void;
  onActivar: (id: number) => void;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [notas, setNotas] = useState('');
  const [historial, setHistorial] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);
  return (
    <div
      className={`rounded-lg border p-4 ${p.activa || !p.obligatoria ? 'border-linea bg-white' : 'border-dorado/50 bg-dorado-claro'}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium">{p.nombre}</h3>
        <span className="text-xs text-apagado">
          {p.formato} · {p.obligatoria ? tr('obligatoria', 'required') : tr('opcional', 'optional')}
        </span>
      </div>
      <p className="mt-1 text-xs text-apagado">{p.descripcion}</p>
      <p className="mt-1 text-xs text-apagado">
        {tr('Se usa en:', 'Used in:')} {p.usadaEn}.
      </p>
      <div className="mt-2 text-sm">
        {p.activa ? (
          <span>
            {tr(`En uso: versión ${numero(p.activa.version)},`, `In use: version ${numero(p.activa.version)},`)}{' '}
            <a href={rutaDePlantilla(p.tipo, undefined, empresaId)} className="text-verde hover:underline" download>
              {p.activa.nombreArchivo}
            </a>{' '}
            <span className="text-xs text-apagado">
              ({tamano(p.activa.tamanoBytes)}, {fechaDeInstante(p.activa.subidoEl)}
              {p.activa.subidoPor ? `, ${p.activa.subidoPor}` : ''})
            </span>
            <NotaDeAyuda texto={D.enUso} />
          </span>
        ) : (
          <span className={p.obligatoria ? 'font-medium' : 'text-apagado'}>
            {p.obligatoria
              ? tr(
                  '⚠ Sin plantilla: los documentos que la necesitan no se pueden generar.',
                  '⚠ No template: the documents that need it cannot be generated.',
                )
              : empresaId
                ? tr('Sin la suya: se usa la general.', 'None of its own: the general one is used.')
                : tr('Sin registrar.', 'Not registered.')}
          </span>
        )}
      </div>
      <form
        className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end"
        onSubmit={e => {
          e.preventDefault();
          if (!archivo) return;
          onSubir(archivo, notas);
          setArchivo(null);
          setNotas('');
          if (entrada.current) entrada.current.value = '';
        }}
      >
        <ZonaParaSoltar>
          <label className="block text-xs text-apagado">
            {p.activa
              ? tr('Subir una versión nueva', 'Upload a new version')
              : tr('Subir la plantilla', 'Upload the template')}
            <NotaDeAyuda texto={D.subir} dentroDeEtiqueta />
            <input
              ref={entrada}
              type="file"
              accept={p.formato}
              onChange={e => setArchivo(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border file:border-linea file:bg-papel file:px-3 file:py-1.5 file:text-sm"
              aria-label={tr(`Archivo para ${p.nombre}`, `File for ${p.nombre}`)}
            />
            {!archivo && <PistaDeSoltar />}
          </label>
        </ZonaParaSoltar>
        <button type="submit" className={boton} disabled={!archivo || pendiente}>
          {tr('Subir', 'Upload')}
        </button>
        <div className="flex items-center gap-1 sm:col-span-2">
          <input
            value={notas}
            onChange={e => setNotas(e.target.value)}
            placeholder={tr('Nota (opcional): qué cambió', 'Note (optional): what changed')}
            className="min-w-0 flex-1 rounded-md border border-linea px-2 py-1.5 text-sm text-tinta"
            aria-label={tr(`Nota para ${p.nombre}`, `Note for ${p.nombre}`)}
          />
          <NotaDeAyuda titulo={tr('Nota', 'Note')} texto={D.nota} />
        </div>
      </form>
      {p.versiones.length > 1 && (
        <div className="mt-2">
          <button type="button" onClick={() => setHistorial(h => !h)} className="text-xs text-verde hover:underline">
            {historial
              ? tr('Ocultar versiones', 'Hide versions')
              : tr(
                  `Ver las ${numero(p.versiones.length)} versiones`,
                  `Show the ${numero(p.versiones.length)} versions`,
                )}
          </button>
          {historial && (
            <ul className="mt-1 space-y-1 text-xs">
              {p.versiones.map(v => (
                <li key={v.id} className="flex flex-wrap items-center gap-2">
                  <span className={v.activa ? 'font-medium' : ''}>
                    v{numero(v.version)} · {v.nombreArchivo} · {fechaDeInstante(v.subidoEl)}
                    {v.subidoPor ? ` · ${v.subidoPor}` : ''}
                    {v.notas ? ` · ${v.notas}` : ''}
                    {v.activa ? tr(' · en uso', ' · in use') : ''}
                  </span>
                  <a href={rutaDePlantilla(p.tipo, v.id, empresaId)} className="text-verde hover:underline" download>
                    {tr('descargar', 'download')}
                  </a>
                  {!v.activa && (
                    <button type="button" className={botonSuave} disabled={pendiente} onClick={() => onActivar(v.id)}>
                      {tr('Volver a usar', 'Use again')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Ajustes() {
  const ajustes = useAjustesDeDocumentos();
  const guardar = useGuardarAjustesDeDocumentos();
  const [valores, setValores] = useState<AjustesDeDocumentos>(VACIO);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [hecho, setHecho] = useState(false);

  useEffect(() => {
    if (ajustes.data?.ajustes) setValores(ajustes.data.ajustes);
  }, [ajustes.data]);

  const enviar = async () => {
    setMensaje(null);
    setHecho(false);
    try {
      await guardar.mutateAsync({
        ...valores,
        consignatario: {
          ...valores.consignatario,
          lineas: valores.consignatario.lineas.map(l => l.trim()).filter(Boolean),
        },
      });
      setHecho(true);
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi
          ? [error.message, ...(error.detalles ?? [])].join(' ')
          : tr('No se pudo guardar.', 'Could not save.'),
      );
    }
  };

  const texto = (etiqueta: string, nota: Texto, valor: string, cambiar: (v: string) => void, ayuda?: string) => (
    <label className="block text-sm">
      <span className="text-xs text-apagado">
        {etiqueta}
        <NotaDeAyuda texto={nota} dentroDeEtiqueta />
      </span>
      <input value={valor} onChange={e => cambiar(e.target.value)} className={campo} aria-label={etiqueta} />
      {ayuda && <span className="mt-0.5 block text-xs text-apagado">{ayuda}</span>}
    </label>
  );

  return (
    <form
      className="space-y-4 rounded-lg border border-linea bg-white p-5"
      onSubmit={e => {
        e.preventDefault();
        void enviar();
      }}
    >
      <div>
        <h2 className="font-medium">{tr('Firmante, vendedor y consignatario', 'Signer, seller and consignee')}</h2>
        <p className="mt-1 text-xs text-apagado">
          {tr(
            'Lo que imprimen los documentos de aduana. Se conserva el historial: cada documento generado sabe con qué datos salió.',
            'What the customs documents print. The history is kept: each generated document knows which data it was made with.',
          )}
          {ajustes.data?.vigenteDesde &&
            tr(
              ` Vigentes desde ${fechaDeInstante(ajustes.data.vigenteDesde)}${ajustes.data.actualizadoPor ? ` (${ajustes.data.actualizadoPor})` : ''}.`,
              ` In effect since ${fechaDeInstante(ajustes.data.vigenteDesde)}${ajustes.data.actualizadoPor ? ` (${ajustes.data.actualizadoPor})` : ''}.`,
            )}
          {ajustes.data &&
            !ajustes.data.configurados &&
            tr(
              ' Todavía no están registrados: los documentos de aduana no se pueden generar.',
              ' They are not registered yet: the customs documents cannot be generated.',
            )}
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {texto(tr('Nombre del firmante', 'Signer name'), D.firmanteNombre, valores.firmante.nombre, v =>
          setValores({...valores, firmante: {...valores.firmante, nombre: v}}),
        )}
        {texto(tr('Cargo del firmante', 'Signer position'), D.firmanteCargo, valores.firmante.cargo, v =>
          setValores({...valores, firmante: {...valores.firmante, cargo: v}}),
        )}
        {texto(
          tr('Nombre corto del firmante', 'Signer short name'),
          D.firmanteNombreCorto,
          valores.firmante.nombreCorto,
          v => setValores({...valores, firmante: {...valores.firmante, nombreCorto: v}}),
          tr('Como va al pie de la carta aclaratoria.', 'As it appears at the foot of the clarification letter.'),
        )}
        {texto(
          tr('Cargo corto del firmante', 'Signer short position'),
          D.firmanteCargoCorto,
          valores.firmante.cargoCorto,
          v => setValores({...valores, firmante: {...valores.firmante, cargoCorto: v}}),
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {texto(tr('Nombre del vendedor', 'Seller name'), D.vendedorNombre, valores.vendedor.nombre, v =>
          setValores({...valores, vendedor: {...valores.vendedor, nombre: v}}),
        )}
        {texto(
          tr('Razón social del vendedor', 'Seller legal name'),
          D.vendedorRazonSocial,
          valores.vendedor.razonSocial,
          v => setValores({...valores, vendedor: {...valores.vendedor, razonSocial: v}}),
        )}
        {texto(tr('Dirección del vendedor', 'Seller address'), D.vendedorDireccion, valores.vendedor.direccion, v =>
          setValores({...valores, vendedor: {...valores.vendedor, direccion: v}}),
        )}
        {texto(tr('Ciudad del vendedor', 'Seller city'), D.vendedorCiudad, valores.vendedor.ciudad, v =>
          setValores({...valores, vendedor: {...valores.vendedor, ciudad: v}}),
        )}
        {texto(
          tr('NIT del vendedor', 'Seller NIT'),
          D.vendedorNit,
          valores.vendedor.nit,
          v => setValores({...valores, vendedor: {...valores.vendedor, nit: v}}),
          tr(
            'Tal como debe imprimirse, con el prefijo "NIT:" y los puntos y el guion del número.',
            'Exactly as it must be printed, with the "NIT:" prefix and the number\'s dots and hyphen.',
          ),
        )}
      </div>
      <div className="space-y-2">
        <span className="text-xs text-apagado">
          {tr(
            'Líneas del consignatario (tal como se imprimen, una por renglón)',
            'Consignee lines (exactly as printed, one per line)',
          )}
          <NotaDeAyuda texto={D.consignatarioLineas} />
        </span>
        {valores.consignatario.lineas.map((linea, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={linea}
              onChange={e => {
                const lineas = [...valores.consignatario.lineas];
                lineas[i] = e.target.value;
                setValores({...valores, consignatario: {...valores.consignatario, lineas}});
              }}
              className={`${campo} mt-0`}
              aria-label={tr(`Línea ${i + 1} del consignatario`, `Consignee line ${i + 1}`)}
            />
            <button
              type="button"
              className={botonSuave}
              disabled={valores.consignatario.lineas.length <= 1}
              onClick={() =>
                setValores({
                  ...valores,
                  consignatario: {
                    ...valores.consignatario,
                    lineas: valores.consignatario.lineas.filter((_, j) => j !== i),
                  },
                })
              }
              aria-label={tr(`Quitar la línea ${i + 1}`, `Remove line ${i + 1}`)}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className={botonSuave}
          onClick={() =>
            setValores({
              ...valores,
              consignatario: {...valores.consignatario, lineas: [...valores.consignatario.lineas, '']},
            })
          }
        >
          + {tr('Agregar línea', 'Add line')}
        </button>
        {texto(
          tr('Consignatario en la carta aclaratoria', 'Consignee in the clarification letter'),
          D.consignatarioCarta,
          valores.consignatario.textoCarta,
          v => setValores({...valores, consignatario: {...valores.consignatario, textoCarta: v}}),
          tr(
            'Texto con el que la carta nombra a la empresa compradora.',
            'Text the letter uses to name the buying company.',
          ),
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {texto(
          tr(
            'Texto de la plantilla Word que se reemplaza por el firmante',
            'Text in the Word template that is replaced by the signer',
          ),
          D.textoFirmante,
          valores.plantillaCarta.textoFirmante,
          v => setValores({...valores, plantillaCarta: {...valores.plantillaCarta, textoFirmante: v}}),
          tr(
            'Debe existir en la carta aclaratoria, en el párrafo de la firma.',
            'It must exist in the clarification letter, in the signature paragraph.',
          ),
        )}
        {texto(
          tr(
            'Texto de la plantilla Word que se reemplaza por el cargo',
            'Text in the Word template that is replaced by the position',
          ),
          D.textoCargo,
          valores.plantillaCarta.textoCargo,
          v => setValores({...valores, plantillaCarta: {...valores.plantillaCarta, textoCargo: v}}),
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" className={boton} disabled={guardar.isPending || ajustes.isPending}>
          {guardar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar', 'Save')}
        </button>
        {mensaje && <span className="text-sm text-alerta">{mensaje}</span>}
        {hecho && <span className="text-sm text-verde-oscuro">{tr('Guardado.', 'Saved.')}</span>}
      </div>
    </form>
  );
}

const tamano = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${numero(bytes / 1024 / 1024, 2)} MB` : `${numero(Math.max(1, Math.round(bytes / 1024)))} KB`;
